import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from 'src/components/ui/button';
import { ConfigurationCriterionBadge } from 'src/features/configuration/configurationCriteria/ConfigurationCriterionBadge';
import { ConfigurationCriteriaIdsEnum } from 'src/features/configuration/configurationCriteria/enums/ConfigurationCriteriaIdsEnum';
import {
  getForecastSessionsQueryKey,
  getSessionForecastDataQueryPrefix,
  getSessionProductsQueryKey,
} from 'src/keys';
import { useAddProductToSession } from 'src/services/api/addProductToOpenSession';
import { useDeleteProductFromSession } from 'src/services/api/deleteProductFromOpenSession';

type ConfigurationReferenceProps = {
  sessionId: string;
  sku: number;
  name: string;
  criteria: string[];
};

export function ConfigurationReference({
  sessionId,
  sku,
  name,
  criteria,
}: ConfigurationReferenceProps): ReactElement {
  const { t } = useTranslation('configuration');
  const queryClient = useQueryClient();
  const { mutate: addProductToSession } = useAddProductToSession({
    onSuccess: () => {
      toast.info('Info', {
        description: t('Product re-added to the session'),
      });
      queryClient.invalidateQueries({
        queryKey: getSessionProductsQueryKey(sessionId),
      });
      queryClient.invalidateQueries({
        queryKey: getSessionForecastDataQueryPrefix(sessionId),
      });
      queryClient.invalidateQueries({
        queryKey: getForecastSessionsQueryKey(),
      });
    },
    onError: () => {
      toast.error(t('Error'), {
        description: t('Error while adding product to the session'),
        action: {
          label: t('Retry'),
          onClick: () => addProductToSession({ sessionId, sku }),
        },
      });
    },
  });
  const { mutate: deleteProductFromSession } = useDeleteProductFromSession({
    onSuccess: () => {
      toast.success(t('Success'), {
        description: t('Product removed from the session'),
        action: {
          label: t('Undo'),
          onClick: () => addProductToSession({ sessionId, sku }),
        },
      });
      queryClient.invalidateQueries({
        queryKey: getSessionProductsQueryKey(sessionId),
      });
      queryClient.invalidateQueries({
        queryKey: getSessionForecastDataQueryPrefix(sessionId),
      });
      queryClient.invalidateQueries({
        queryKey: getForecastSessionsQueryKey(),
      });
    },
    onError: () => {
      toast.error(t('Error'), {
        description: t('Error while deleting product'),
        action: {
          label: t('Retry'),
          onClick: () => deleteProductFromSession({ sessionId, sku }),
        },
      });
    },
  });

  return (
    <div className='flex flex-row items-center justify-between gap-2'>
      <div className='flex flex-row items-center justify-between gap-2'>
        <span className='text-sm font-semibold'>{sku}</span>
        <span className='hidden max-w-80 truncate text-nowrap text-sm font-normal text-layout-foreground-secondary lg:block'>
          {name}
        </span>
      </div>
      <div className='flex flex-row items-center justify-between gap-4'>
        <div className='flex flex-row items-center justify-between gap-2'>
          {criteria.map((criterion) => (
            <ConfigurationCriterionBadge
              key={criterion}
              id={
                ConfigurationCriteriaIdsEnum[
                  criterion as keyof typeof ConfigurationCriteriaIdsEnum
                ]
              }
            />
          ))}
        </div>
        <Button
          variant='ghost'
          size='icon'
          onClick={() => deleteProductFromSession({ sessionId, sku })}
        >
          <X className='h-4 w-4' />
        </Button>
      </div>
    </div>
  );
}
